import { DynamicYield } from '@packages/shared/src/ui/DynamicYield/DynamicYield';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { AddToBasketButtonTailwind as AddToBasketButton } from '@packages/modules/src/AddToBasketButton/AddToBasketButtonTailwind.client';
import { MiniDVLayer } from '@packages/modules/src/MiniDVLayer/MiniDVLayer.client';
import { useQuery } from 'urql';
import { getProductAndFallbackId } from '@packages/modules/src/helpers/getProductAndFallbackId/getProductAndFallbackId';
import { MiniDetailViewDataDocument } from '@packages/gql/generated/shopping/MiniDetailViewDataDocument';
import { Button } from '@packages/shared/src/ui/Button/Button';

export interface PremiumBannerProps {
  articleNumber?: string;
  mode: 'Basket' | 'CheckAndOrder';
}
const messages = defineMessages({
  customFlexiButtonText: {
    id: 'PremiumBanner.Flexikonto.Button.Text',
    defaultMessage: 'Mitglied werden',
  },
  premiumBannerText: {
    id: 'PremiumBanner.Text',
    defaultMessage: 'Versandkosten-Flatrate 1 Jahr{break} oder 2 Jahre',
  },
  premiumBannerInfo: {
    id: 'PremiumBanner.Info',
    defaultMessage:
      'Jetzt Vorteilsclub-Mitglied werden und <strong>sofort alle Versand- & Speditionskosten sparen</strong> für nur 30€/Jahr.*',
  },
  premiumBannerInfoLegalText: {
    id: 'PremiumBanner.Info.LegalText',
    defaultMessage:
      '*Die Mitgliedschaft ist jederzeit kündbar und verlängert sich bei Nichtkündigung jährlich.',
  },
});

export const PremiumBanner: React.FC<PremiumBannerProps> = ({ articleNumber, mode }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    order: {
      hasFlexikontoAdvantageClub,
      assets: {
        premium: { getPremiumIcon },
      },
    },
    product: {
      aplus: { enabled: aplusEnabled },
    },
    dynamicYieldEnabled,
  } = useConfig();
  const { locale, language } = useI18n();
  const { formatMessage } = useIntl();
  const [{ data, fetching }] = useQuery({
    query: MiniDetailViewDataDocument,
    variables: {
      ...getProductAndFallbackId(`${articleNumber}-0`, undefined),
      locale,
      lang: language,
      skipAplus: !aplusEnabled,
    },
    context: useMemo(() => ({ suspense: false }), []),
    // this will only be called if flexikonto is enabled
    pause: !hasFlexikontoAdvantageClub,
  });

  const productData = data?.product;

  // CheckAndOrder step is not implemented yet so we return null
  if (!articleNumber || mode === 'CheckAndOrder') {
    return null;
  }
  const shouldShowPremiumIcon = getPremiumIcon && !hasFlexikontoAdvantageClub;
  const shouldShowDefaultPremiumIcon = !getPremiumIcon && !hasFlexikontoAdvantageClub;
  const flexDirection = hasFlexikontoAdvantageClub ? 'flex-col' : 'flex-row';
  return (
    <div className="px-1 py-2">
      {dynamicYieldEnabled && mode === 'Basket' && <DynamicYield dataDyId="dy_basket_premium" />}
      <div className={`flex items-center justify-center gap-2 ${flexDirection}`}>
        {shouldShowPremiumIcon && (
          <div className="mr-0.5 w-5.5">
            <Image src={getPremiumIcon({ language })} alt="premium icon" width={40} height={40} />
          </div>
        )}
        {shouldShowDefaultPremiumIcon && (
          <div className="bg-color-secondary mr-2 flex h-3.5 w-3.5 items-center justify-around">
            <div className="icon-emp-premiumlounge w-2.5" />
          </div>
        )}
        {!hasFlexikontoAdvantageClub && (
          <Button
            size="medium"
            variant="link"
            onClick={() => {
              setIsModalOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setIsModalOpen(true);
              }
            }}
          >
            <div className="text-size-body-sm">
              {formatMessage(messages.premiumBannerText, { break: <br /> })}
            </div>
          </Button>
        )}
        {hasFlexikontoAdvantageClub && (
          <>
            <div className="text-size-body-xs">
              {formatMessage(messages.premiumBannerInfo, {
                strong: (...chunks: ReactNode[]) => <strong>{chunks}</strong>,
              })}
            </div>
            {productData && !fetching && (
              <AddToBasketButton
                trackingProductData={{
                  akl: productData.akl,
                  availability: productData.availability,
                  sku: productData.sku,
                  brand: productData.brand,
                  defaultCategory: productData.defaultCategory,
                  orderNumber: productData.orderNumber,
                  dimensions: productData.dimensions,
                  longDescription: productData.longDescription,
                  marketcode: productData.marketcode,
                  media: productData.media,
                  name: productData.name,
                  promotion: productData.promotion,
                  price: productData.price,
                }}
                addToBasketTrackingAplus={productData.aplus || undefined}
                color="default"
                size="medium"
                className="bg-color-grey text-color-text-light w-auto"
                customButtonText={messages.customFlexiButtonText}
              />
            )}
            <div className="text-size-body-xs">
              {formatMessage(messages.premiumBannerInfoLegalText)}
            </div>
          </>
        )}
      </div>
      <MiniDVLayer
        open={isModalOpen}
        sku={articleNumber}
        onClose={() => setIsModalOpen(false)}
        quantity={1}
        akl=""
      />
    </div>
  );
};
